import React, { useRef, useImperativeHandle } from 'react';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { OP_DOCUMENTS_DISPLAY_MODE } from '@components/operatorPanel/constants/operatorPanel.constants';
import { ImageUpload, isWeb, navigateBack, navigationRef, shareFile } from '@helpers';
import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import { addImageModal, getImageContext } from '@components/imageHandling/common';
import { ImageHandlingDisplayMode } from '@v2/components/imageHandling/types/types.d';
import { HeaderIconKey, IconPositions, ImageHandlingHeaderIconConfig, UseManageHeaderIconsParams } from '@v2/components/imageHandling/components/Header/components/HeaderIcons/types';
import { useAnnotationModeEnabled } from '@v2/components/imageHandling/components/DrawingCanvas/hooks';

export const useManageHeaderIcons = ({
  img,
  shouldHideIconsWithoutImg,
  shouldHideDownloadButton,
  shouldHideAttachButton,
  shouldHideThumbnailButton,
  shouldShowAddButtonIfSingleImage,
  shouldShowAddImageModal,
  shouldDisplayModeIcon,
  isSingleImage,
  download,
  imageContextForAdding,
  triggerCrop,
  triggerDraw,
  isDrawing,
  rotateImage,
  setMode,
  showAttachModal,
  showInfoPopUp,
  showRotatePopUp,
  toggleAttachModal,
  triggerInfoPopUp,
  triggerRotatePopUp,
  navigateBackFallBackScreen,
  onClose,
  onSuccessImageUpload,
  onExpandIconPress,
  onCollapseIconPress,
  scale = initialScale,
  font = initialFont,
  C,
  ref,
  enabledIcons,
}: UseManageHeaderIconsParams) => {
  const imgExist = typeof img === 'object' && Object.keys(img)?.length > 0;
  const isDocument = img?.isDocument && imgExist;
  const shouldHideIcon = !!shouldHideIconsWithoutImg && !imgExist;

  const isAnnotationModeEnabled = useAnnotationModeEnabled();

  // Position tracking refs
  const iconRefs = useRef<{ [key: string]: View | null }>({});
  const iconPositions = useRef<IconPositions>({});

  // Expose positions through imperative handle
  useImperativeHandle(ref, () => iconPositions.current, []);

  // Function to register an icon ref
  const registerIconRef = (key: string | undefined, viewRef: View | null) => {
    if (key && viewRef) {
      iconRefs.current[key] = viewRef;
    }
  };

  const measureIconAndTrigger = (key: string, callback: () => void) => {
    const viewRef = iconRefs.current[key];
    if (!viewRef) {
      callback();
      return;
    }
    viewRef.measureInWindow((x, y) => {
      iconPositions.current[key] = { x, y };
      callback();
    });
  };

  const hoveredStyle = ({
    isModalOpen,
    hovered,
    pressed,
    disabled,
  }: {
    hovered: boolean;
    pressed: boolean;
    isModalOpen?: boolean;
    disabled?: boolean;
  }): StyleProp<ViewStyle> => {
    return {
      width: scale(36),
      height: scale(36),
      backgroundColor: disabled
        ? 'transparent'
        : pressed || isModalOpen
        ? C('Button/Light/Only Text pressed')
        : hovered
        ? C('Button/Light/Only Text hover')
        : 'transparent',
      borderRadius: scale(36) / 2,
      justifyContent: 'center',
      alignItems: 'center',
    };
  };

  const onDisplayIconPress = () => {
    navigationRef.current?.setParams({ mode: OP_DOCUMENTS_DISPLAY_MODE.TILE });
  };

  const iconsConfigMap: Record<HeaderIconKey, Omit<ImageHandlingHeaderIconConfig, 'key'>> = {
    [HeaderIconKey.CROP]: {
      shouldDisplay: !shouldHideIcon && !isDocument,
      onPress: !img?.isProcessing ? triggerCrop : () => {},
      icon: 'crop',
      width: scale(23),
      height: scale(23),
      color: C('Link/Light/Primary'),
      disabled: !!isDrawing,
      containerStyles: {
        marginTop: scale(4),
      },
    },
    [HeaderIconKey.ROTATE]: {
      shouldDisplay: !shouldHideIcon && !isDocument,
      onPress:
        img.isPDF && img.pageCount > 1
          ? () => measureIconAndTrigger('rotateIcon', triggerRotatePopUp)
          : () => rotateImage({ documentId: img.documentId, onePage: true }),
      isModalOpened: showRotatePopUp,
      icon: 'rotate',
      width: scale(21),
      height: scale(23),
      disabled: !!isDrawing,
      color: C('Link/Light/Primary'),
      trackPositionKey: 'rotateIcon',
    },
    [HeaderIconKey.DRAW]: {
      shouldDisplay: !shouldHideIcon && !isDocument && isAnnotationModeEnabled,
      onPress: triggerDraw ?? (() => {}),
      isModalOpened: !!isDrawing,
      icon: 'annotations',
      width: scale(22),
      height: scale(22),
      color: C('Link/Light/Primary'),
      trackPositionKey: 'drawIcon',
    },
    [HeaderIconKey.SHARE]: {
      shouldDisplay: !shouldHideIcon && !shouldHideDownloadButton,
      onPress: isWeb ? download : () => shareFile({ setLoading: () => {}, url: img?.url }),
      icon: 'share',
      width: scale(24),
      height: scale(24),
      disabled: !!isDrawing,
      color: C('Link/Light/Primary'),
    },
    [HeaderIconKey.ATTACH]: {
      shouldDisplay: !shouldHideIcon && !shouldHideAttachButton,
      onPress: toggleAttachModal,
      icon: 'clip',
      width: scale(19.5),
      height: scale(22),
      disabled: !!isDrawing,
      color: C('Link/Light/Primary'),
      isModalOpened: showAttachModal,
      containerStyles: {
        position: 'relative',
      },
      elementToTheRightOfIcon: (
        <Text
          style={[
            font['Small'],
            {
              position: 'absolute',
              left: scale(26),
              top: scale(18),
              color: C('Link/Light/Primary'),
              pointerEvents: 'none',
            },
          ]}
        >
          {img?.links?.length ?? 0}
        </Text>
      ),
    },
    [HeaderIconKey.THUMBNAIL]: {
      shouldDisplay: !shouldHideIcon && !isSingleImage && !isDocument && !shouldHideThumbnailButton && !!setMode,
      onPress: () => setMode?.(ImageHandlingDisplayMode.THUMBNAIL),
      icon: 'squares',
      width: scale(22),
      height: scale(22),
      disabled: !!isDrawing,
      color: C('Link/Light/Primary'),
    },
    [HeaderIconKey.ADD_IMAGE]: {
      shouldDisplay: !isSingleImage || !!shouldShowAddButtonIfSingleImage,
      onPress: () =>
        imageContextForAdding
          ? ImageUpload({
              saveToMainStore: true,
              onSuccessImageUpload,
              ...imageContextForAdding,
              ...imageContextForAdding,
            })
          : addImageModal(getImageContext()),
      icon: 'plus',
      width: scale(20),
      height: scale(20),
      disabled: !!isDrawing,
      color: C(shouldShowAddImageModal ? 'Button/Light/Secondary' : 'Link/Light/Primary'),
    },
    [HeaderIconKey.DISPLAY_MODE]: {
      shouldDisplay: !!shouldDisplayModeIcon,
      onPress: onDisplayIconPress,
      icon: 'pictureUnderline',
      width: scale(24),
      height: scale(28),
      color: C('Link/Light/Primary'),
      containerStyles: {
        width: scale(40),
        height: scale(40),
      },
    },
    [HeaderIconKey.MORE]: {
      shouldDisplay: !shouldHideIcon,
      onPress: () => measureIconAndTrigger('moreIcon', triggerInfoPopUp),
      isModalOpened: showInfoPopUp,
      icon: 'more',
      width: scale(20),
      height: scale(22),
      disabled: !!isDrawing,
      color: C('Link/Light/Primary'),
      trackPositionKey: 'moreIcon',
    },
    [HeaderIconKey.CLOSE]: {
      shouldDisplay: true,
      onPress: onClose ? onClose : () => navigateBack(navigateBackFallBackScreen),
      icon: 'closeBold',
      width: scale(20),
      height: scale(20),
      disabled: !!isDrawing,
      color: C('Link/Light/Primary'),
    },
    [HeaderIconKey.COLLAPSE]: {
      shouldDisplay: true,
      onPress: onCollapseIconPress ? () => onCollapseIconPress() : () => navigateBack(navigateBackFallBackScreen),
      icon: 'zoomOut',
      width: scale(20),
      height: scale(20),
      disabled: !!isDrawing,
      color: C('Link/Light/Primary'),
    },
    [HeaderIconKey.EXPAND]: {
      shouldDisplay: !!onExpandIconPress,
      onPress: () => onExpandIconPress?.(),
      icon: 'expandView',
      width: scale(18),
      height: scale(18),
      disabled: !!isDrawing,
      color: C('Link/Light/Primary'),
    },
  };

  // Default enabled icons if not specified
  const defaultEnabledIcons: HeaderIconKey[] = [
    HeaderIconKey.CROP,
    HeaderIconKey.ROTATE,
    HeaderIconKey.DRAW,
    HeaderIconKey.SHARE,
    HeaderIconKey.THUMBNAIL,
    HeaderIconKey.ADD_IMAGE,
    HeaderIconKey.DISPLAY_MODE,
    HeaderIconKey.MORE,
    HeaderIconKey.CLOSE,
    HeaderIconKey.COLLAPSE,
    HeaderIconKey.EXPAND,
  ];

  const activeIcons =
    enabledIcons && !enabledIcons.includes(HeaderIconKey.COLLAPSE) && !enabledIcons.includes(HeaderIconKey.CLOSE)
      ? [...enabledIcons, HeaderIconKey.CLOSE]
      : enabledIcons || defaultEnabledIcons;

  const iconsConfig: ImageHandlingHeaderIconConfig[] = activeIcons
    .map((iconKey) => {
      const config = iconsConfigMap[iconKey];
      if (!config) return null;
      return {
        key: iconKey,
        ...config,
      };
    })
    .filter((config): config is ImageHandlingHeaderIconConfig => config !== null);

  return { hoveredStyle, iconsConfig, registerIconRef };
};
